'use client';

import { ReactNode, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useRouter } from '@/i18n/routing';
import { pagesRoutes } from '@/shared/constants/routes';
import { MessageBox } from '@/shared/components/message-box';
import { LoginForm } from '@/features/auth/internal/components/login/login-form';
import { CALLBACK_URL_PARAM, SOCIAL_AUTH_PROVIDER_PARAM } from '@/shared/constants/search-params';
import { GoogleLink } from '@/features/auth/shared/components/google-link';
import { FormSeparator } from '@/features/auth/internal/components/form-separator';
import { getProviderDisplayName } from '@/features/auth/shared/helpers/get-provider-display-name';
import { AccountProvider } from '@/features/auth/shared/types/account-providers';
import { Button } from '@/shared/components/primitives/button';
import {
  SOCIAL_LOGIN_ERROR_PARAM,
  SOCIAL_LOGIN_ERRORS,
} from '@/shared/types/errors/social-auth-errors';
import { useIsWTGamesApp } from '@/features/games-app-bridge/shared/hooks/use-is-wt-games-app';
import { emitGTMEvent } from '@/features/gtm/shared/helper/emit-gtm-event';
import { EMAIL_VERIFIED_COOKIE } from '@/features/auth/shared/helpers/constants/verification-cookies';
import { readClientCookie, deleteClientCookie } from '@/shared/utils/client-cookies';
import { FacebookLink } from '@/features/auth/shared/components/facebook-link';

type LoginFormContainerProps = {
  titleId: string;
};

const socialLoginErrorValues = Object.values(SOCIAL_LOGIN_ERRORS) as string[];

export const LoginFormContainer = ({ titleId }: LoginFormContainerProps) => {
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const isGamesApp = useIsWTGamesApp();
  const t = useTranslations('Auth.login');

  const redirectUrl = searchParams.get(CALLBACK_URL_PARAM) || '';
  const callbackUrl = redirectUrl || pagesRoutes.home.href;

  const [verifiedEmail] = useState(() => readClientCookie(EMAIL_VERIFIED_COOKIE));

  useEffect(() => {
    if (verifiedEmail) {
      deleteClientCookie(EMAIL_VERIFIED_COOKIE);
    }
  }, [verifiedEmail]);

  const socialLoginError = searchParams.get(SOCIAL_LOGIN_ERROR_PARAM);
  const isValidSocialError = socialLoginError && socialLoginErrorValues.includes(socialLoginError);

  const social_provider = searchParams.get(SOCIAL_AUTH_PROVIDER_PARAM);

  const navigateWithCallback = (url: string) => {
    const urlWithCallback = redirectUrl ? `${url}?${CALLBACK_URL_PARAM}=${redirectUrl}` : url;
    router.push(urlWithCallback);
  };

  const handleCreateAccountClick = () => {
    navigateWithCallback(pagesRoutes.register.href);
  };

  const getSocialLoginErrorContent = (): ReactNode | null => {
    if (!isValidSocialError) {
      return null;
    }

    emitGTMEvent('USER_SOCIAL_LOGIN_FAILED', {
      social_error: socialLoginError,
      social_provider,
    });

    if (socialLoginError === SOCIAL_LOGIN_ERRORS.NOT_REGISTERED) {
      return t.rich(`form.errors.socialLogin.${socialLoginError}`, {
        signUp: (chunks) => (
          <Button
            type='button'
            variant='noStyle'
            size='noStyle'
            className='text-secondary hover:!text-secondary-hover underline'
            onClick={() => {
              handleCreateAccountClick();
            }}
          >
            {chunks}
          </Button>
        ),
      });
    }

    return t(`form.errors.socialLogin.${socialLoginError}`);
  };

  const socialLoginErrorContent = isGamesApp ? null : getSocialLoginErrorContent();
  const displayError: ReactNode | null = error ?? socialLoginErrorContent;

  const errorMessageTitle = t('form.errors.messageBoxTitle');

  const displayNameFacebook = getProviderDisplayName(AccountProvider.FACEBOOK);
  const displayNameGoogle = getProviderDisplayName(AccountProvider.GOOGLE);

  return (
    <div className='flex w-full flex-col items-center px-4'>
      {verifiedEmail && (
        <div className='w-full max-w-96'>
          <MessageBox type='success' className='mb-4 w-full' closable={false}>
            {t('emailVerified')}
          </MessageBox>
        </div>
      )}
      {displayError && (
        <div role='alert'>
          <MessageBox type='error' className='mb-4 w-full' title={errorMessageTitle}>
            {displayError}
          </MessageBox>
        </div>
      )}
      {!verifiedEmail && (
        <>
          <div className='mt-4 flex w-full max-w-96 flex-col gap-2'>
            <FacebookLink
              intent='login'
              buttonText={`${t('form.continueWith')} ${displayNameFacebook}`}
              callbackUrl={callbackUrl}
              className='min-h-[46px]'
            />
            <GoogleLink
              intent='login'
              buttonText={`${t('form.continueWith')} ${displayNameGoogle}`}
              callbackUrl={callbackUrl}
              className='min-h-[46px]'
            />
          </div>
          <FormSeparator text={t('form.orSignInWithEmail')} />
        </>
      )}
      <LoginForm
        setRootError={setError}
        titleId={titleId}
        accountCreationHandler={() => {
          handleCreateAccountClick();
        }}
        redirectUrl={redirectUrl}
        defaultEmail={verifiedEmail || ''}
      />
    </div>
  );
};
